import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, User, Building2, TrendingUp, CheckCircle, BookOpen, Target, Award } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { getTransitionTypeLabel } from '../../models/TransitionType'

const PositionModal = ({ position, isOpen, onClose }) => {
  const [showExtended, setShowExtended] = useState(false)
  
  if (!position) return null

  // Анимации для модального окна
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  }

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8, y: 50 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: { type: "spring", damping: 25, stiffness: 300 }
    },
    exit: { opacity: 0, scale: 0.8, y: 50 }
  }

  // Определение контекста просмотра (админ / публичный)
  const location = useLocation()
  const isAdminView = location.pathname.includes('/admin')

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Подложка */}
          <motion.div
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Модальное окно */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Заголовок */}
              <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-6 py-4 border-b border-primary-200">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary-500 rounded-lg">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {position.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {position.department}{isAdminView && ` • Уровень ${position.level}`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-white hover:bg-opacity-50 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Содержимое */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                <div className="space-y-6">
                  {/* Описание должности */}
                  {position.description && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <Building2 className="w-4 h-4 mr-2" />
                        Описание должности
                      </h4>
                      <p className="text-gray-600 leading-relaxed">
                        {position.description}
                      </p>
                    </div>
                  )}

                  {/* Ключевые компетенции */}
                  {position.competencies && position.competencies.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Ключевые компетенции
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {position.competencies.map((competency, index) => (
                          <div
                            key={index}
                            className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg"
                          >
                            <CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{competency}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Возможности карьерного роста */}
                  {position.careerPaths && position.careerPaths.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                        <TrendingUp className="w-4 h-4 mr-2" />
                        Возможности карьерного роста
                      </h4>
                      <div className="space-y-2">
                        {position.careerPaths.map((path, index) => (
                          <div
                            key={index}
                            className="p-3 bg-gradient-to-r from-secondary-50 to-secondary-100 rounded-lg border border-secondary-200"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-800">{path.title}</span>
                              <span className="text-xs px-2 py-1 bg-secondary-500 text-white rounded-full">
                                {getTransitionTypeLabel(path.type)}
                              </span>
                            </div>
                            {path.requirements && (
                              <p className="text-xs text-gray-600 mt-1">
                                Требования: {path.requirements}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Дополнительная информация */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      Дополнительная информация
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Отдел:</span>
                        <span className="ml-2 text-gray-700">{position.department_name || position.department}</span>
                      </div>
                      {isAdminView && (
                        <div>
                          <span className="text-gray-500">Уровень:</span>
                          <span className="ml-2 text-gray-700">{position.level}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Расширенная информация (показывается по клику "Подробнее") */}
                  {showExtended && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ duration: 0.3 }}
                      className="space-y-6"
                    >
                      {/* Требования к должности */}
                      {position.requirements_detailed && position.requirements_detailed.trim() && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            <Target className="w-4 h-4 mr-2" />
                            Требования к кандидату
                          </h4>
                          <div className="bg-blue-50 rounded-lg p-4">
                            <div className="text-sm text-gray-700 whitespace-pre-line">
                              {position.requirements_detailed}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Обязанности */}
                      {position.responsibilities && position.responsibilities.trim() && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            <BookOpen className="w-4 h-4 mr-2" />
                            Ключевые обязанности
                          </h4>
                          <div className="bg-green-50 rounded-lg p-4">
                            <div className="text-sm text-gray-700 whitespace-pre-line">
                              {position.responsibilities}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Условия и льготы */}
                      {position.benefits && position.benefits.trim() && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            <Award className="w-4 h-4 mr-2" />
                            Условия работы и льготы
                          </h4>
                          <div className="bg-purple-50 rounded-lg p-4">
                            <div className="text-sm text-gray-700 whitespace-pre-line">
                              {position.benefits}
                            </div>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>
              </div>

              {/* Футер */}
              <div className="px-6 py-4 bg-gray-50 border-t">
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                  >
                    Закрыть
                  </button>
                  <button 
                    onClick={() => setShowExtended(!showExtended)}
                    className="btn-primary"
                  >
                    {showExtended ? 'Свернуть' : 'Подробнее'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

export default PositionModal 